<template>
  <div class="connections-wrapper animated fadeIn">
    <div v-if="!connections" class="text-center py-5">
      <b-spinner label="Loading..." variant="primary"></b-spinner>
      <p class="text-muted mt-2">Analyzing identity links...</p>
    </div>

    <b-card v-else class="border-secondary shadow">
      <template #header>
        <div class="d-flex justify-content-between align-items-center">
            <span class="h5 mb-0">
                <i class="fa fa-user-secret text-danger mr-2"></i><strong>Ownership & Identity Analysis</strong>
            </span>
            <div class="d-flex align-items-center">
                <b-button size="sm" variant="info" class="mr-3 shadow-sm font-weight-bold" v-b-modal.risk-info>
                    <i class="fa fa-question-circle mr-1"></i> Scoring Guide
                </b-button>
                <b-badge variant="primary" pill class="px-3 py-2">{{ connections.length }} Results</b-badge>
            </div>
        </div>
      </template>

      <!-- Legend -->
      <div class="mb-3 d-flex justify-content-end small text-white-50">
          <span class="mr-2"><b-badge variant="danger" class="mr-1 shadow-sm">100k</b-badge> Email</span>
          <span class="mr-2"><b-badge variant="warning" class="mr-1 shadow-sm text-dark">50k</b-badge> Device</span>
          <span class="mr-2"><b-badge variant="success" class="mr-1">10k</b-badge> Registration</span>
          <span class="mr-2"><b-badge variant="dark" class="mr-1">5k</b-badge> MAC</span>
          <span><b-badge variant="primary" class="mr-1 opacity-75">5k</b-badge> IP</span>
      </div>

      <b-table
        hover
        responsive
        :items="connections"
        :fields="fields"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        show-empty
        empty-text="No identity links found."
        class="mb-0"
      >
        <!-- Risk Score -->
        <template #cell(risk_score)="data">
           <div class="d-flex align-items-center justify-content-center">
               <b-badge :variant="getRiskVariant(data.item.identity_score)" class="p-2" style="min-width: 55px; font-size: 0.9rem;">
                 {{ data.item.identity_score | formatScore }}
               </b-badge>
           </div>
        </template>

        <!-- Username -->
        <template #cell(username)="data">
            <div class="d-flex flex-column">
                <router-link :to="'/user/' + data.value + '?server=' + server" class="font-weight-bold">
                    {{ data.value }}
                </router-link>
                <div class="small mt-1">
                    <b-badge v-if="data.item.banned" variant="danger" class="mr-1">Banned</b-badge>
                    <span v-if="data.item.online" class="text-success"><i class="fa fa-circle small"></i> Online</span>
                    <span v-else class="text-muted"><i class="fa fa-circle-o small"></i> Offline</span>
                </div>
            </div>
        </template>

        <!-- Email -->
        <template #cell(email)="data">
            <div class="text-muted small">{{ data.value }}</div>
        </template>

        <!-- Matches (Badges) -->
        <template #cell(matches)="data">
            <div class="d-flex flex-wrap">
                <b-badge v-if="hasMatch(data.item.matches, 'Email')" variant="danger" class="mr-1 mb-1">Email</b-badge>
                <b-badge v-if="hasMatch(data.item.matches, 'Device')" variant="warning" class="mr-1 mb-1 text-dark">Device</b-badge>
                <b-badge v-if="hasMatch(data.item.matches, 'CreateIP')" variant="success" class="mr-1 mb-1">Registration</b-badge>
                <b-badge v-if="hasMatch(data.item.matches, 'MAC')" variant="dark" class="mr-1 mb-1">MAC</b-badge>
                <b-badge v-if="hasMatch(data.item.matches, 'IP')" variant="primary" class="mr-1 mb-1 opacity-75">IP</b-badge>
            </div>
        </template>

        <!-- Details Toggle -->
        <template #cell(details)="row">
          <b-button size="sm" @click="row.toggleDetails" variant="transparent" class="text-muted">
            <i :class="row.detailsShowing ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
          </b-button>
        </template>

        <!-- Expanded Details -->
        <template #row-details="row">
          <div class="p-3">
             <b-row>
               <b-col md="12">
                 <h6 class="text-danger font-weight-bold text-uppercase small mb-2">
                    <i class="fa fa-exclamation-triangle mr-1"></i> Identity Matches
                 </h6>
                 <b-table
                    small
                    :items="row.item.matches"
                    :fields="matchFields"
                    class="shadow-sm rounded mb-0"
                    bordered
                    show-empty
                    sort-by="date"
                    :sort-desc="true"
                    empty-text="No identity matches."
                 >
                    <template #cell(type)="data">
                        <span v-if="data.value === 'CreateIP'" class="font-weight-bold text-success">Registration</span>
                        <span v-else-if="data.value === 'Email'" class="font-weight-bold text-danger">Email</span>
                        <span v-else-if="data.value === 'Device'" class="font-weight-bold text-warning">Device</span>
                        <span v-else-if="data.value === 'MAC'" class="font-weight-bold text-white">MAC</span>
                        <span v-else class="font-weight-bold text-primary">{{ data.value }}</span>
                    </template>
                    <template #cell(value)="data">
                        <div v-if="canExpand(data.item.type)">
                            <a href="#" @click.prevent="showShared(data.item.type, data.value)" class="text-primary font-weight-bold">
                               {{ data.value }} <i class="fa fa-search small ml-1"></i>
                            </a>
                        </div>
                        <span v-else>{{ data.value }}</span>
                    </template>
                    <template #cell(date)="data">{{ data.value | formatDate }}</template>
                 </b-table>
               </b-col>
             </b-row>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Risk Info Modal -->
    <b-modal id="risk-info" title="Identity Score Guide" hide-footer body-class="bg-dark" header-bg-variant="dark" header-text-variant="white">
        <div class="p-2 text-white">

            <table class="table table-sm table-dark table-striped border-secondary mb-4">
                <thead>
                    <tr class="text-muted small text-uppercase">
                        <th>Factor</th>
                        <th class="text-center">Points</th>
                        <th>Meaning</th>
                    </tr>
                </thead>
                <tbody class="text-light">
                    <tr>
                        <td><b-badge variant="danger">Email</b-badge></td>
                        <td class="text-center"><strong class="text-danger">100,000</strong></td>
                        <td><strong>Definite Match:</strong> Same email address.</td>
                    </tr>
                    <tr>
                        <td><b-badge variant="warning" class="text-dark">Device</b-badge></td>
                        <td class="text-center"><strong class="text-warning">50,000</strong></td>
                        <td><strong>Very Strong:</strong> Confidential System Match.</td>
                    </tr>
                    <tr>
                        <td><b-badge variant="success">Registration</b-badge></td>
                        <td class="text-center"><strong class="text-success">10,000</strong></td>
                        <td><strong>Strong:</strong> Created on the same internet connection.</td>
                    </tr>
                    <tr>
                        <td><b-badge variant="dark">MAC</b-badge></td>
                        <td class="text-center"><span class="text-white-50">Dynamic</span></td>
                        <td><strong>Hardware:</strong> Shared computer/hardware.</td>
                    </tr>
                    <tr>
                        <td><b-badge variant="primary">IP</b-badge></td>
                        <td class="text-center"><span class="text-white-50">Dynamic</span></td>
                        <td><strong>Behavioral:</strong> Shared internet connection.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </b-modal>


  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'ConnectionsView',
  props: {
    username: { type: String, required: true },
    server: { type: String, required: true }
  },
  data() {
    return {
      sortBy: 'risk_score',
      sortDesc: true,
      modalTitle: 'Shared Accounts',
      fields: [
        { key: 'risk_score', label: 'Score', sortable: true, class: 'text-center' },
        { key: 'username', label: 'Linked Account', sortable: true },
        { key: 'email', label: 'Email', sortable: true },
        { key: 'matches', label: 'Identity Links' },
        { key: 'last_active', label: 'Last Active', sortable: true, formatter: val => {
            if (!val || val.startsWith('0001') || val.startsWith('0000')) return '-';
            return moment(val).fromNow();
        }},
        { key: 'details', label: '', class: 'text-right' }
      ],
      matchFields: [
         { key: 'type', label: 'Type', sortable: true },
         { key: 'value', label: 'Identifier', sortable: true },
         { key: 'count', label: '#', sortable: true },
         { key: 'date', label: 'Last Seen', sortable: true }
      ],
      sharedFields: [
          { key: 'username', label: 'Username', sortable: true },
          { key: 'count', label: 'Logins', sortable: true },
          { key: 'email', label: 'Email' },
          { key: 'money', label: 'Money', sortable: true, formatter: val => {
            return val ? val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') : '0'
          }},
          { key: 'date', label: 'Last Seen', sortable: true }
      ]
    }
  },
  computed: {
    ...mapGetters({
        connections: 'identityConnections',
        sharedIPs: 'sharedIPs'
    })
  },
  watch: {
    username: 'fetchData',
    server: 'fetchData'
  },
  created() {
    this.fetchData()
  },
  methods: {
    fetchData() {
        if (this.username && this.server) {
             this.$store.commit('setIdentity', null)
             this.emit('getIdentity', {
                username: this.username,
                server: this.server
             })
        }
    },
    getRiskVariant(score) {
        if (score >= 10000) return 'dark';
        return 'secondary';
    },
    hasMatch(matches, type) {
        if (!matches) return false;
        return matches.some(m => m.type === type);
    },
    canExpand(type) {
        return ['IP', 'MAC', 'CreateIP'].includes(type);
    },
    showShared(type, value) {
        this.$store.commit('setSharedIPs', null);
        let event = '';
        let payload = { server: this.server };

        if (type === 'IP' || type === 'RegIP' || type === 'CreateIP') {
            this.modalTitle = `Accounts sharing IP: ${value}`;
            event = 'getSharedIPs';
            payload.ip = value;
        } else if (type === 'MAC') {
            this.modalTitle = `Accounts sharing MAC: ${value}`;
            event = 'getSharedMACs';
            payload.mac = value;
        }

        if (event) {
            this.emit(event, payload);
            this.$root.$emit('show-shared-modal', {
                title: this.modalTitle,
                server: this.server
            });
        }
    }
  },
  filters: {
      formatDate(val) {
          if (!val || val.startsWith('0001') || val.startsWith('0000')) return '-';
          return moment(val).format('YYYY-MM-DD');
      },
      formatScore(val) {
          if (val === undefined || val === null) return '0';
          return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      }
  }
}
</script>

<style scoped>
.connections-wrapper {
    min-height: 400px;
}
.opacity-75 {
    opacity: 0.75;
}
.cursor-pointer {
    cursor: pointer;
}

/deep/ .close {
    color: #fff;
    text-shadow: none;
    opacity: 0.7;
}
/deep/ .close:hover {
    opacity: 1;
}
</style>
